// Document properties: the Info dictionary a reader shows in "File →
// Properties", plus what the file's encryption currently allows and what
// this editor will do to the next export.
//
// Editing writes through the worker and rebases, so a changed title is
// undoable like any other document edit — and because mupdf only writes
// the Info dictionary, the worker patches the XMP mirror too (see
// lib/xmp.ts). The panel says so when the file carries XMP.
import { useEffect, useMemo, useState } from "react";
import type {
  DocInfo,
  DocInfoFields,
  SecurityOptions,
} from "../lib/protocol.ts";
import { describePermissions, formatPdfDate } from "../lib/doc-security.ts";

const FIELDS: { key: keyof DocInfoFields; label: string; hint?: string }[] = [
  { key: "title", label: "Title" },
  { key: "author", label: "Author" },
  { key: "subject", label: "Subject" },
  { key: "keywords", label: "Keywords", hint: "Comma-separated" },
  { key: "creator", label: "Creator", hint: "The application the content came from" },
  { key: "producer", label: "Producer", hint: "The software that wrote the PDF" },
];

function blank(): DocInfoFields {
  return { title: "", author: "", subject: "", keywords: "", creator: "", producer: "" };
}

function pick(info: DocInfo | null): DocInfoFields {
  if (!info) return blank();
  return {
    title: info.title,
    author: info.author,
    subject: info.subject,
    keywords: info.keywords,
    creator: info.creator,
    producer: info.producer,
  };
}

/** One-line description of what the next export will do about security. */
export function describeExportSecurity(s: SecurityOptions, sourceEncrypted: boolean): string {
  if (s.mode === "encrypt") {
    return s.userPassword
      ? "Exports are encrypted and need a password to open"
      : "Exports open freely but restrict what viewers may do";
  }
  if (s.mode === "none") {
    return sourceEncrypted ? "Exports have the password removed" : "Exports are unencrypted";
  }
  return sourceEncrypted ? "Exports keep the original encryption" : "Exports are unencrypted";
}

export function PropertiesPanel({
  info,
  security,
  busy,
  paneWidth,
  onClose,
  onApply,
  onEditSecurity,
}: {
  info: DocInfo | null;
  security: SecurityOptions;
  busy: boolean;
  paneWidth: number;
  onClose: () => void;
  onApply: (fields: Partial<DocInfoFields>) => void;
  onEditSecurity: () => void;
}) {
  const committed = useMemo(() => pick(info), [info]);
  const [draft, setDraft] = useState<DocInfoFields>(committed);

  // A rebase (this panel's own apply, or an undo) replaces `info` — the
  // draft follows the document rather than shadowing it.
  useEffect(() => {
    setDraft(committed);
  }, [committed]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const changed = (Object.keys(committed) as (keyof DocInfoFields)[]).filter(
    (k) => draft[k] !== committed[k]
  );

  const rows: [string, string][] = info
    ? [
        ["Pages", String(info.pageCount)],
        ["PDF version", info.format || "—"],
        ["Created", info.creationDate ? formatPdfDate(info.creationDate) : "—"],
        ["Modified", info.modDate ? formatPdfDate(info.modDate) : "—"],
        ["Encryption", info.encryption],
      ]
    : [];

  return (
    <div
      className="flex min-h-0 w-full flex-1 flex-col bg-[color:var(--color-bg)]"
      style={{ width: paneWidth }}
    >
      <div className="flex items-center gap-2 border-b border-[color:var(--color-border)] p-3">
        <i className="ri-file-info-line text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
        <h2 className="text-sm font-semibold">Properties</h2>
        <button
          type="button"
          title="Close"
          aria-label="Close properties"
          onClick={onClose}
          className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
        >
          <i className="ri-close-line" aria-hidden />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {!info && <p className="text-xs text-[color:var(--color-fg-muted)]">Reading properties…</p>}

        {info && (
          <>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (changed.length === 0 || busy) return;
                onApply(Object.fromEntries(changed.map((k) => [k, draft[k]])));
              }}
              className="space-y-2.5"
            >
              {FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <span className="mb-1 block text-[11px] font-medium text-[color:var(--color-fg-muted)]">
                    {f.label}
                  </span>
                  <input
                    value={draft[f.key]}
                    placeholder={f.hint ?? "—"}
                    onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                    // The editor's global shortcuts must not fire while
                    // someone types a title containing "t" or "h".
                    onKeyDown={(e) => e.stopPropagation()}
                    className="h-8 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg)] px-2 text-xs"
                  />
                </label>
              ))}

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="submit"
                  disabled={changed.length === 0 || busy}
                  className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-accent)] bg-[color:var(--color-accent)] px-3 text-xs text-[color:var(--color-accent-fg)] hover:opacity-90 disabled:opacity-50"
                >
                  <i className="ri-check-line" aria-hidden /> Apply
                </button>
                <button
                  type="button"
                  disabled={changed.length === 0}
                  onClick={() => setDraft(committed)}
                  className="inline-flex h-8 items-center rounded-md border border-[color:var(--color-border)] px-3 text-xs hover:bg-[color:var(--color-bg-subtle)] disabled:opacity-40"
                >
                  Revert
                </button>
                {changed.length > 0 && (
                  <span className="text-[11px] text-[color:var(--color-fg-muted)]">
                    {changed.length} unsaved
                  </span>
                )}
              </div>
            </form>

            {info.hasXmp && (
              <p className="mt-3 rounded-md bg-[color:var(--color-bg-subtle)] p-2 text-[11px] text-[color:var(--color-fg-muted)]">
                This file also carries an XMP metadata packet. Applying updates both copies, so
                readers that prefer XMP show the same values.
              </p>
            )}

            <dl className="mt-4 space-y-1 border-t border-[color:var(--color-border)] pt-3">
              {rows.map(([k, v]) => (
                <div key={k} className="flex gap-2 text-[11px]">
                  <dt className="w-24 shrink-0 text-[color:var(--color-fg-muted)]">{k}</dt>
                  <dd className="min-w-0 flex-1 break-words">{v}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-4 border-t border-[color:var(--color-border)] pt-3">
              <div className="flex items-center gap-2">
                <i className="ri-shield-keyhole-line text-[color:var(--color-fg-muted)]" aria-hidden />
                <h3 className="text-xs font-semibold">Security</h3>
                <button
                  type="button"
                  onClick={onEditSecurity}
                  className="ml-auto inline-flex h-7 items-center rounded-md border border-[color:var(--color-border)] px-2 text-[11px] hover:bg-[color:var(--color-bg-subtle)]"
                >
                  Change…
                </button>
              </div>
              <p className="mt-1.5 text-[11px] text-[color:var(--color-fg-muted)]">
                This file: {describePermissions(info.permissions).toLowerCase()}.
              </p>
              <p className="text-[11px] text-[color:var(--color-fg-muted)]">
                {describeExportSecurity(security, info.encryption !== "None")}.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
